import { DropdownOptionGroup } from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import { useCallback, useMemo, useState } from 'react'
import {
  isVendorArchived,
  Vendor,
  VendorStatus,
} from 'src/queries/vendors/vendors.types'
import { InfiniteScrollProps } from 'src/types'

export interface VendorOptionDetails {
  vendor: Vendor
}

/**
 * A vendor is payable only when it is ACTIVE **and** currently has an ACH
 * payment method. The Vendor projection (list and detail) joins the payment
 * method on `is_current`, so retiring one nulls this out. ACTION_REQUIRED
 * vendors have no account on file, and ARCHIVED vendors cannot receive new
 * payments, so neither appears here.
 *
 * Scoped to the Vendor projection deliberately: the standalone
 * `GET /vendors/{id}/payment-methods/{pmId}` endpoint does NOT filter on
 * `is_current` and will happily return a retired method. That is how past
 * payments keep their bank label, and it is not a payability signal.
 */
export const isPayableVendor = (vendor: Vendor): boolean =>
  !isVendorArchived(vendor) &&
  vendor.status === VendorStatus.Active &&
  vendor.paymentMethod != null

/** Case-insensitive substring match over the vendor's name AND email. */
const matchesQuery = (vendor: Vendor, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  return (
    vendor.name.toLowerCase().includes(q) ||
    vendor.email.toLowerCase().includes(q)
  )
}

interface UseVendorSelectOptionsResult {
  optionGroups: DropdownOptionGroup<VendorOptionDetails>[]
  infiniteScrollProps: InfiniteScrollProps<string>
  hasPayableVendors: boolean
  searchQuery: string
  resetSearch: () => void
}

/**
 * Maps the vendor list into dropdown option models for `VendorSelect`, applying
 * the payable filter and the name+email search.
 *
 * `useDropdownLogic`'s built-in filter only matches an option's `label`, so it
 * cannot search on email. We therefore bypass it — its filter is skipped
 * whenever `infiniteScrollProps` is present — and do the filtering here instead
 * (the same shape `CreditCardDropdownV2` uses). The dropdown's search box drives
 * `searchQuery` through the `fetch` callback; there is no network call, the
 * vendor list is already in memory.
 */
export const useVendorSelectOptions = (
  vendors: Vendor[],
  isLoading: boolean,
): UseVendorSelectOptionsResult => {
  const [searchQuery, setSearchQuery] = useState('')

  const payableVendors = useMemo(
    () => vendors.filter(isPayableVendor),
    [vendors],
  )

  const optionGroups = useMemo<DropdownOptionGroup<VendorOptionDetails>[]>(
    () => [
      {
        id: 'vendors',
        options: payableVendors
          .filter((vendor) => matchesQuery(vendor, searchQuery))
          .map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
            sublabel: vendor.email,
            details: { vendor },
          })),
      },
    ],
    [payableVendors, searchQuery],
  )

  const infiniteScrollProps = useMemo<InfiniteScrollProps<string>>(
    () => ({
      // `useDropdownLogic` calls this on every (debounced) search keystroke with
      // page 0. We use it purely to lift the search text into local state — there
      // is no server request; the filtering above is entirely client-side.
      fetch: (filterInput, page) => {
        if (page === 0) setSearchQuery(filterInput ?? '')
      },
      page: 0,
      loading: isLoading,
      complete: true,
    }),
    [isLoading],
  )

  const resetSearch = useCallback(() => setSearchQuery(''), [])

  return {
    optionGroups,
    infiniteScrollProps,
    hasPayableVendors: payableVendors.length > 0,
    searchQuery,
    resetSearch,
  }
}
